import { get, post, put, del } from '../client/axiosClient';
import { employeeEndpoints } from '../endpoints/employee.endpoints';
import { buildEndpointWithQuery } from '../endpoints/base.endpoints';
import type {
  ApiResponse, PaginatedResponse, EmptyResponse, ID,
  Employee, CreateEmployeeRequest, UpdateEmployeeRequest, EmployeeSearchRequest,
  TerminationPreview, TerminateEmployeeRequest,
} from '../types';

/**
 * Module ids accepted by the /Employees/scoped and /Employees/visible endpoints.
 * Must stay in sync with EmployeesController.ScopedModuleIds on the backend.
 */
export type ScopedModuleId =
  | 'timesheet-entry'
  | 'engagement-management'
  | 'disbursement-entry'
  | 'employee-activity-report'
  | 'wip-report'
  | 'timesheet-status-report'
  | 'manager-dashboard';

export interface ScopedEmployeeQuery extends EmployeeSearchRequest {
  moduleId: ScopedModuleId;
}

function unpackPaged(raw: any): PaginatedResponse<Employee> {
  const pagedResult = raw?.data ?? {};
  return {
    success: raw?.success ?? false,
    message: raw?.message,
    data: pagedResult.items ?? [],
    pagination: {
      currentPage: pagedResult.page ?? 1,
      pageSize: pagedResult.pageSize ?? 20,
      totalPages: pagedResult.totalPages ?? 0,
      totalItems: pagedResult.totalCount ?? 0,
      hasNextPage: pagedResult.hasNext ?? false,
      hasPreviousPage: pagedResult.hasPrevious ?? false,
    },
  };
}

class EmployeeService {
  async getAll(params?: EmployeeSearchRequest): Promise<PaginatedResponse<Employee>> {
    const endpoint = params
      ? buildEndpointWithQuery(employeeEndpoints.basePath(), params as Record<string, any>)
      : employeeEndpoints.list();
    const response = await get<any>(endpoint);
    return unpackPaged(response.data);
  }

  /**
   * Action-scope list — employees the caller is allowed to ACT on (manage-*).
   * Used by transaction Add/Edit dropdowns.
   */
  async getScoped(params: ScopedEmployeeQuery): Promise<PaginatedResponse<Employee>> {
    const endpoint = buildEndpointWithQuery(`${employeeEndpoints.basePath()}/scoped`, params as Record<string, any>);
    const response = await get<any>(endpoint);
    return unpackPaged(response.data);
  }

  /**
   * View-scope list — employees the caller is allowed to SEE (view-*).
   * Used by list filters, report filters, dashboards.
   */
  async getVisible(params: ScopedEmployeeQuery): Promise<PaginatedResponse<Employee>> {
    const endpoint = buildEndpointWithQuery(`${employeeEndpoints.basePath()}/visible`, params as Record<string, any>);
    const response = await get<any>(endpoint);
    return unpackPaged(response.data);
  }

  async getById(id: ID): Promise<ApiResponse<Employee>> {
    const response = await get<ApiResponse<Employee>>(employeeEndpoints.getById(id));
    return response.data;
  }

  async getByCode(code: string): Promise<ApiResponse<Employee>> {
    const response = await get<ApiResponse<Employee>>(employeeEndpoints.getByCode(code));
    return response.data;
  }

  async create(data: CreateEmployeeRequest): Promise<ApiResponse<Employee>> {
    const response = await post<ApiResponse<Employee>>(employeeEndpoints.create(), data);
    return response.data;
  }

  async update(id: ID, data: UpdateEmployeeRequest): Promise<ApiResponse<Employee>> {
    const response = await put<ApiResponse<Employee>>(employeeEndpoints.update(id), data);
    return response.data;
  }

  async delete(id: ID): Promise<EmptyResponse> {
    await del<void>(employeeEndpoints.delete(id));
    return { success: true };
  }

  // ===========================================================================
  //  Termination
  // ===========================================================================

  /**
   * Fetch the termination preview for the given employee + candidate
   * last-working-day (YYYY-MM-DD). The modal uses this to show blockers and
   * planned auto-submit weeks before the partner confirms.
   */
  async getTerminationPreview(id: ID, dateLeft: string): Promise<ApiResponse<TerminationPreview>> {
    const endpoint = buildEndpointWithQuery(
      `${employeeEndpoints.basePath()}/${id}/termination-preview`,
      { dateLeft },
    );
    const response = await get<ApiResponse<TerminationPreview>>(endpoint);
    return response.data;
  }

  async terminate(id: ID, data: TerminateEmployeeRequest): Promise<ApiResponse<Employee>> {
    const response = await post<ApiResponse<Employee>>(employeeEndpoints.terminate(id), data);
    return response.data;
  }
}

export const employeeService = new EmployeeService();
export default employeeService;
